import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle, Handshake, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const CONSULT_IMG = "https://images.unsplash.com/photo-1579389083046-e3df9c2b3325?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMHRlY2glMjBjb25zdWx0YXRpb258ZW58MHx8fHwxNzc2NTI3Njk3fDA&ixlib=rb-4.1.0&q=85";

const TIME_SLOTS = [
  "Mornings (9-12 UK)",
  "Afternoons (13-17 UK)",
  "Evenings (17-19 UK)",
  "Flexible",
];

// Friendly headline / sub copy per known referral source so the form feels contextual
function contextFor(source, demo) {
  if (source === "co-innovation") {
    return {
      badge: "Co-innovation programme",
      title: "Apply for a founding-partner slot.",
      sub: "Tell us your trade and the daily friction. If it's a fit, we'll book the design sprint within a fortnight.",
      topicHint: "Co-innovation founding-partner application",
    };
  }
  if (source === "co-innovation-waitlist") {
    return {
      badge: "Co-innovation · waitlist",
      title: "Join the next round of founding partners.",
      sub: "All slots taken for this period — we'll let you know the moment a place opens.",
      topicHint: "Co-innovation waitlist",
    };
  }
  if (demo) {
    return {
      badge: `From: ${demo}`,
      title: `Talk to us about a build like ${demo}.`,
      sub: "We'll talk through how it would work for your specific situation — no slide decks, no hard sell.",
      topicHint: `Tailored demo: ${demo}`,
    };
  }
  return null;
}

export default function Consultation() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const demoParam = params.get("demo") || "";
  const sourceParam = params.get("source") || (demoParam ? `interest_${demoParam}` : "consultation");
  const promoParam = params.get("promo") || (typeof window !== "undefined" ? (() => { try { return localStorage.getItem("appistry.promo") || ""; } catch (_) { return ""; } })() : "");

  const ctx = contextFor(sourceParam, demoParam);

  // Consultation is event/programme-exclusive. Public direct visits with no context land on /contact.
  const hasContext = Boolean(ctx) || Boolean(promoParam) || Boolean(demoParam);
  useEffect(() => {
    if (!hasContext) navigate("/contact", { replace: true });
  }, [hasContext, navigate]);

  const [form, setForm] = useState({
    lead_name: "",
    lead_email: "",
    company: "",
    topic: ctx?.topicHint || "",
    duration_minutes: 30,
    preferred_times: [],
    preferred_contact: "email",
    notes: "",
    consent_privacy: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const toggleSlot = (slot) => {
    setForm((f) => ({
      ...f,
      preferred_times: f.preferred_times.includes(slot)
        ? f.preferred_times.filter((s) => s !== slot)
        : [...f.preferred_times, slot],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_privacy) {
      toast.error("Please tick the privacy consent to continue.");
      return;
    }
    setSubmitting(true);
    try {
      // /consultations now creates the lead AND captures source/demo/promo in one call
      await api.post("/consultations", {
        ...form,
        source: sourceParam,
        referring_demo: demoParam || null,
        promo_code: promoParam || null,
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="app-container py-32 text-center" data-testid="consultation-success">
        <CheckCircle size={48} weight="fill" className="text-[hsl(var(--appistry-signal))] mx-auto mb-6" />
        <h1 className="text-4xl md:text-5xl font-black tracking-tighter mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Request received.</h1>
        <p className="text-lg text-zinc-600 max-w-lg mx-auto">We'll come back to you to lock in a time that works. Check your inbox (including spam).</p>
      </div>
    );
  }

  return (
    <div data-testid="consultation-page">
      <section className="border-b border-zinc-200 grid grid-cols-1 lg:grid-cols-12">
        <div className="lg:col-span-7 p-10 md:p-20">
          {ctx && (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-5 bg-zinc-950 text-white" data-testid="consult-context-badge">
              <Handshake size={14} weight="fill" className="text-[hsl(var(--appistry-signal))]" />
              <span className="text-[10px] font-mono uppercase tracking-[2.5px] font-bold">{ctx.badge}</span>
            </div>
          )}
          {promoParam === "hl1may" && (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-5 ml-2 bg-[hsl(var(--appistry-signal))] text-white" data-testid="consult-promo-badge">
              <span className="text-[10px] font-mono uppercase tracking-[2.5px] font-bold">★ Networking-event guest · free 30-min</span>
            </div>
          )}
          <p className="overline mb-6">Consultation</p>
          <h1 className="text-5xl md:text-6xl font-black tracking-tighter leading-none max-w-2xl" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {ctx?.title || "30 minutes. One shippable idea."}
          </h1>
          <p className="text-lg text-zinc-600 mt-6 max-w-xl">
            {ctx?.sub || "Tell us about the friction. We'll name the smallest build that would actually help — and whether we're the right people for it."}
          </p>
          <ul className="mt-8 space-y-3">
            {["No slide deck on our end", "No slide deck required from you", "No hard sell — if it's not a fit, we'll say so"].map((t) => (
              <li key={t} className="flex items-start gap-3 text-zinc-700">
                <CheckCircle size={20} weight="fill" className="text-[hsl(var(--appistry-signal))] mt-0.5" />
                {t}
              </li>
            ))}
          </ul>
          <p className="text-xs font-mono text-zinc-400 mt-8" data-testid="consult-source-debug">
            Tracked source: <span className="text-zinc-700 font-semibold">{sourceParam}</span>
            {demoParam && <> · demo: <span className="text-zinc-700 font-semibold">{demoParam}</span></>}
            {promoParam && <> · promo: <span className="text-zinc-700 font-semibold">{promoParam}</span></>}
          </p>
        </div>
        <div className="hidden lg:flex lg:col-span-5 relative bg-zinc-950 items-center justify-center overflow-hidden">
          <div className="absolute inset-0 grid-dots opacity-10" />
          <div className="absolute top-[-20%] right-[-20%] w-[500px] h-[500px] rounded-full opacity-40 blur-3xl" style={{ background: "radial-gradient(circle, hsl(var(--appistry-signal)) 0%, hsl(var(--appistry-teal)) 50%, transparent 80%)" }} />
          <div className="relative text-center text-white p-10">
            <img src="/appistry-logo.png" alt="Appistry" className="w-28 h-28 object-contain mx-auto mb-8 opacity-95" />
            <p className="text-4xl font-black tracking-tighter mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>30<span className="text-[hsl(var(--appistry-teal))]">:</span>00</p>
            <p className="text-xs font-mono uppercase tracking-widest text-zinc-400">One call · One plan · One shippable idea</p>
          </div>
        </div>
      </section>

      <section>
        <div className="app-container py-16 md:py-20 max-w-3xl">
          <form onSubmit={submit} className="space-y-6" data-testid="consultation-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Full name" required>
                <input required value={form.lead_name} onChange={(e) => setForm({ ...form, lead_name: e.target.value })} className="input-appistry" data-testid="consult-name" />
              </Field>
              <Field label="Work email" required>
                <input required type="email" value={form.lead_email} onChange={(e) => setForm({ ...form, lead_email: e.target.value })} className="input-appistry" data-testid="consult-email" />
              </Field>
            </div>
            <Field label="Company">
              <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="input-appistry" data-testid="consult-company" />
            </Field>
            <Field label="What's the topic?" required hint="One sentence is fine — we'll unpack on the call.">
              <input required value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} className="input-appistry" data-testid="consult-topic" />
            </Field>
            <Field label="Preferred times">
              <div className="flex flex-wrap gap-2">
                {TIME_SLOTS.map((s) => {
                  const selected = form.preferred_times.includes(s);
                  return (
                    <button type="button" key={s} onClick={() => toggleSlot(s)} className={`px-4 py-2 text-sm border ${selected ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"}`} data-testid={`consult-slot-${s}`}>
                      {s}
                    </button>
                  );
                })}
              </div>
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Duration">
                <select value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: Number(e.target.value) })} className="input-appistry" data-testid="consult-duration">
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>60 minutes</option>
                </select>
              </Field>
              <Field label="Preferred contact">
                <select value={form.preferred_contact} onChange={(e) => setForm({ ...form, preferred_contact: e.target.value })} className="input-appistry" data-testid="consult-contact">
                  <option value="email">Email</option>
                  <option value="phone">Phone</option>
                  <option value="video">Video call</option>
                </select>
              </Field>
            </div>
            <Field label="Anything else we should know?">
              <textarea rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-appistry" data-testid="consult-notes" />
            </Field>
            <label className="flex items-start gap-3 cursor-pointer text-sm text-zinc-700">
              <input type="checkbox" checked={form.consent_privacy} onChange={(e) => setForm({ ...form, consent_privacy: e.target.checked })} className="mt-1" data-testid="consult-consent" />
              I'm happy for Appistry to store these details and contact me about this request. I can request deletion at any time.
            </label>
            <button disabled={submitting} className="btn-signal disabled:opacity-60" data-testid="consult-submit">
              {submitting ? "Sending…" : "Request consultation"} <ArrowRight size={16} weight="bold" />
            </button>
          </form>
        </div>
      </section>

      <style>{`
        .input-appistry {
          width: 100%;
          background: #fff;
          border: 1px solid hsl(var(--appistry-border));
          padding: 0.8rem 1rem;
          font-family: 'Manrope', sans-serif;
          font-size: 0.95rem;
          transition: border-color 150ms ease, box-shadow 150ms ease;
        }
        .input-appistry:focus {
          outline: none;
          border-color: hsl(var(--appistry-signal));
          box-shadow: 0 0 0 3px rgba(225,29,72,0.12);
        }
      `}</style>
    </div>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-zinc-800">
          {label}{required && <span className="text-[hsl(var(--appistry-signal))] ml-1">*</span>}
        </span>
        {hint && <span className="text-xs text-zinc-500">{hint}</span>}
      </div>
      {children}
    </label>
  );
}
